import PropTypes from 'prop-types'
import { forwardRef } from 'react'
import Clickable from './Clickable'
import cn from '../modules/cn'
import WithAddon from './WithAddon'

const Button = forwardRef(({
  children,
  leadingAddon,
  trailingAddon,
  addonProps,
  onClick,
  to,
  href,
  primary,
  neutral,
  large,
  small,
  fullWidth,
  disabled,
  className,
  ...props
}, ref) => {
  const baseStyle = cn(
    'w-full sm:w-auto h-10 px-2 rounded-full text-base font-bold',
    'relative flex justify-center items-center',
    'focus:outline-none select-none',
    'ring-0 ring-offset-0 focus:ring-offset-1 focus:ring-1',
  )

  const neutralStyle = cn(
    'bg-transparent text-gray-600',
    'hover:bg-gray-50 hover:text-gray-700 active:bg-gray-100',
    'active:text-gray-800 focus:bg-gray-100 focus:ring-gray-100 focus:text-gray-800',
  )
  const defaultStyle = cn(
    'bg-theme-100 text-theme-600',
    'hover:bg-theme-200 hover:text-theme-700 active:bg-theme-300',
    'active:text-theme-800 focus:bg-theme-300 focus:ring-theme-300',
    'focus:text-theme-800',
  )
  const primaryStyle = cn(
    'bg-theme-500 text-white',
    'hover:bg-theme-600 hover:text-white active:bg-theme-700',
    'active:text-white focus:bg-theme-700 focus:ring-theme-700 focus:text-white',
  )

  const style = cn(
    baseStyle,
    defaultStyle,
    neutral && neutralStyle,
    primary && primaryStyle,
    small && 'h-8 px-1.5 text-sm',
    large && 'h-14 px-3 text-lg',
    children && 'px-3.5',
    small && children && 'px-2.5',
    large && children && 'px-4',
    fullWidth && 'w-full sm:w-full',
    !children && 'w-auto',
    disabled && 'opacity-50 pointer-events-none',
    className,
  )

  const { className: addonClassName, ...addonRestProps } = addonProps

  return (
    <Clickable
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...props}
      to={to}
      href={href}
      onClick={onClick}
      disabled={disabled}
      className={style}
      ref={ref}
    >
      <WithAddon
        leadingAddon={leadingAddon}
        trailingAddon={trailingAddon}
        wrapperClassName="justify-center"
        fullWidth={fullWidth}
        className={[large ? 'px-2' : 'px-1.5', fullWidth && 'justify-start', addonClassName]}
        leadingAddonClassName={large ? 'text-[2em]' : 'text-[1.5em]'}
        trailingAddonClassName={large ? 'text-[2em]' : 'text-[1.5em]'}
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...addonRestProps}
      >
        {children && children}
      </WithAddon>
    </Clickable>
  )
})

Button.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  leadingAddon: PropTypes.node,
  trailingAddon: PropTypes.node,
  addonProps: PropTypes.shape(WithAddon.PropTypes),
  onClick: PropTypes.func,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
    hash: PropTypes.string,
  })]),
  href: PropTypes.string,
  primary: PropTypes.bool,
  neutral: PropTypes.bool,
  large: PropTypes.bool,
  small: PropTypes.bool,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  className: cn.propType,
}

Button.defaultProps = {
  children: null,
  leadingAddon: null,
  trailingAddon: null,
  addonProps: {},
  onClick: null,
  to: null,
  href: null,
  primary: false,
  neutral: false,
  large: false,
  small: false,
  fullWidth: false,
  disabled: false,
  className: null,
}

export default Button
